import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import NotWiredBanner from '../../components/NotWiredBanner';
import PageHeader from '../../components/PageHeader';
import usePageTitle from '../../hooks/usePageTitle';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { useBranding } from '../../contexts/BrandingContext';
import { PRESET_THEMES } from '../../themes/presets';
import { cn } from '@/lib/utils';
import {
  Palette,
  Type,
  Image,
  Save,
  RefreshCw,
  CheckCircle,
  Lock,
  Sparkles,
  AlertCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : '/api';

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('accessToken')}`
});

/* Tailwind needs every class spelled out, so the plan chip tones are full strings (the old
 * `bg-${plan}-500/20` template never produced a generated class). enterprise = accent,
 * professional = primary, anything else = muted. */
const PLAN_TONE = {
  enterprise: 'border-accent/30 bg-accent/10 text-accent',
  professional: 'border-primary/30 bg-primary/10 text-primary',
  basic: 'border-border bg-muted/40 text-muted-foreground',
};

const LABEL = 'text-xs font-medium uppercase tracking-wide text-muted-foreground';
const INPUT = 'border-border bg-secondary/60 placeholder:text-muted-foreground/70';

function ThemeCard({ theme, isSelected, onSelect, isLocked }) {
  const colors = theme.colors || {};

  return (
    <button
      type="button"
      onClick={() => !isLocked && onSelect(theme.id)}
      disabled={isLocked}
      aria-pressed={isSelected}
      className={cn(
        'tap relative min-w-0 rounded-xl border-2 p-4 text-left transition-all',
        isSelected
          ? 'border-primary bg-primary/10'
          : isLocked
            ? 'cursor-not-allowed border-border bg-secondary/30 opacity-60'
            : 'border-border bg-secondary/60 hover:border-primary/40'
      )}
      data-testid={`theme-${theme.id}`}
    >
      {isLocked && (
        <div className="absolute right-2 top-2">
          <Lock className="h-4 w-4 text-muted-foreground" />
        </div>
      )}

      <div className="mb-3 flex gap-1">
        {['primary', 'secondary', 'accent', 'background'].map(key => (
          <div
            key={key}
            className="h-6 w-6 rounded-full border border-border"
            style={{ backgroundColor: colors[key] || '#666' }}
            title={key}
          />
        ))}
      </div>

      <h4 className="truncate font-medium text-foreground">{theme.name}</h4>
      <p className="mt-1 text-xs text-muted-foreground">{theme.description}</p>

      {isSelected && (
        <CheckCircle className="absolute bottom-2 right-2 h-5 w-5 text-primary" />
      )}

      {isLocked && (
        <Badge variant="outline" className="mt-2 border-warning/50 text-xs text-warning">
          Requires {theme.minPlan}
        </Badge>
      )}
    </button>
  );
}

function ColorPicker({ label, value, onChange, disabled }) {
  return (
    <div className="min-w-0 space-y-2">
      <Label className={LABEL}>{label}</Label>
      <div className="flex min-w-0 gap-2">
        <div
          className="h-10 w-10 shrink-0 rounded-lg border border-border"
          style={{ backgroundColor: value }}
        />
        <Input
          type="text"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="#06b6d4"
          disabled={disabled}
          className={cn(INPUT, 'h-10 min-w-0 flex-1 font-mono text-sm')}
        />
        <Input
          type="color"
          value={value || '#000000'}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          aria-label={`${label} picker`}
          className={cn(INPUT, 'h-10 w-10 shrink-0 cursor-pointer p-1')}
        />
      </div>
    </div>
  );
}

/* First-load skeleton in the page's own shape: header row, tab strip, one card of theme tiles. */
function BrandingSkeleton() {
  const block = 'animate-pulse rounded-xl bg-secondary/60';
  return (
    <div className="space-y-6" aria-busy="true" aria-label="Loading branding settings" data-testid="branding-skeleton">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-2">
          <div className={cn(block, 'h-7 w-48')} />
          <div className={cn(block, 'h-4 w-72 max-w-full')} />
        </div>
        <div className={cn(block, 'h-9 w-36')} />
      </div>
      <div className={cn(block, 'h-9 w-full max-w-md')} />
      <div className="aurora-card p-5 sm:p-6">
        <div className={cn(block, 'h-4 w-32')} />
        <div className={cn(block, 'mt-2 h-3 w-56 max-w-full')} />
        <div className="mt-5 grid grid-cols-1 gap-3 xs:grid-cols-2 sm:gap-4 md:grid-cols-3 xl:grid-cols-4">
          {[0, 1, 2, 3].map(i => <div key={i} className={cn(block, 'h-28')} />)}
        </div>
      </div>
    </div>
  );
}

function BrandingSettingsPageInner() {
  usePageTitle('Branding');
  const queryClient = useQueryClient();
  const { refreshBranding } = useBranding();
  const [activeTab, setActiveTab] = useState('theme');

  const [formData, setFormData] = useState({
    app_name: '',
    app_short_name: '',
    tagline: '',
    logo_url: '',
    favicon_url: '',
    theme: 'dark-cyan',
    colors: {},
    fonts: {},
    custom_css: ''
  });

  // Fetch current branding
  const { data: brandingData, isLoading } = useQuery({
    queryKey: ['myBranding'],
    queryFn: () => axios.get(`${API_URL}/tenants/my/branding`, { headers: getAuthHeaders() }).then(res => res.data)
  });

  // Fetch available themes
  const { data: themesData } = useQuery({
    queryKey: ['themes'],
    queryFn: () => axios.get(`${API_URL}/tenants/themes`, { headers: getAuthHeaders() }).then(res => res.data)
  });

  const plan = brandingData?.plan || 'basic';
  const planFeatures = brandingData?.plan_features || {};
  const availableThemes = themesData?.themes || Object.values(PRESET_THEMES);

  // Update form when data loads
  useEffect(() => {
    if (brandingData?.branding) {
      const b = brandingData.branding;
      setFormData({
        app_name: b.app_name || '',
        app_short_name: b.app_short_name || '',
        tagline: b.tagline || '',
        logo_url: b.logo_url || '',
        favicon_url: b.favicon_url || '',
        theme: b.theme || 'dark-cyan',
        colors: b.colors || {},
        fonts: b.fonts || {},
        custom_css: b.custom_css || ''
      });
    }
  }, [brandingData]);

  const saveMutation = useMutation({
    mutationFn: (data) => axios.put(`${API_URL}/tenants/my/branding`, data, { headers: getAuthHeaders() }),
    onSuccess: () => {
      toast.success('Branding saved successfully');
      queryClient.invalidateQueries(['myBranding']);
      refreshBranding();
    },
    onError: (err) => toast.error(err.response?.data?.detail || 'Failed to save branding')
  });

  const handleSave = () => {
    // Only send fields that have values
    const dataToSend = {};
    if (formData.app_name) dataToSend.app_name = formData.app_name;
    if (formData.app_short_name) dataToSend.app_short_name = formData.app_short_name;
    if (formData.tagline) dataToSend.tagline = formData.tagline;
    if (formData.logo_url) dataToSend.logo_url = formData.logo_url;
    if (formData.favicon_url) dataToSend.favicon_url = formData.favicon_url;
    if (formData.theme) dataToSend.theme = formData.theme;
    if (planFeatures.color_picker && Object.keys(formData.colors).length > 0) {
      dataToSend.colors = formData.colors;
    }
    if (planFeatures.custom_css && formData.custom_css) {
      dataToSend.custom_css = formData.custom_css;
    }

    saveMutation.mutate(dataToSend);
  };

  const handleThemeSelect = (themeId) => {
    setFormData({ ...formData, theme: themeId });
    // Also update colors from theme preset
    const theme = PRESET_THEMES[themeId];
    if (theme) {
      setFormData(prev => ({
        ...prev,
        theme: themeId,
        colors: { ...theme.colors }
      }));
    }
  };

  if (isLoading) {
    return <BrandingSkeleton />;
  }

  return (
    <div className="space-y-6" data-testid="branding-settings-page">
      <PageHeader
        icon={Palette}
        title="Branding Settings"
        description="Customize how your platform appears to users"
        actions={(
          <>
            <Badge variant="outline" className={cn('rounded-full', PLAN_TONE[plan] || PLAN_TONE.basic)} data-testid="plan-badge">
              {plan.charAt(0).toUpperCase() + plan.slice(1)} Plan
            </Badge>
            <Button onClick={handleSave} disabled={saveMutation.isPending} className="tap shadow-glow" data-testid="save-branding-btn">
              {saveMutation.isPending ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Save Changes
            </Button>
          </>
        )}
      />

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        {/* wraps onto two rows on narrow phones instead of pushing the page sideways */}
        <TabsList className="flex h-auto w-full flex-wrap justify-start gap-1 rounded-xl bg-secondary/60 p-1 sm:inline-flex sm:w-auto">
          <TabsTrigger value="theme" className="tap rounded-lg">
            <Palette className="mr-2 h-4 w-4" />
            Theme
          </TabsTrigger>
          <TabsTrigger value="branding" className="tap rounded-lg">
            <Image className="mr-2 h-4 w-4" />
            Branding
          </TabsTrigger>
          <TabsTrigger value="colors" disabled={!planFeatures.color_picker} className="tap rounded-lg">
            <Sparkles className="mr-2 h-4 w-4" />
            Colors
            {!planFeatures.color_picker && <Lock className="ml-1 h-3 w-3" />}
          </TabsTrigger>
          <TabsTrigger value="advanced" disabled={!planFeatures.custom_css} className="tap rounded-lg">
            <Type className="mr-2 h-4 w-4" />
            Advanced
            {!planFeatures.custom_css && <Lock className="ml-1 h-3 w-3" />}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="theme" className="space-y-6">
          <Card className="aurora-card">
            <CardHeader className="p-5 sm:p-6">
              <CardTitle className="font-heading text-base font-semibold">Select Theme</CardTitle>
              <CardDescription>
                Choose a preset theme for your platform
              </CardDescription>
            </CardHeader>
            <CardContent className="p-5 pt-0 sm:p-6 sm:pt-0">
              <div className="grid grid-cols-1 gap-3 xs:grid-cols-2 sm:gap-4 md:grid-cols-3 xl:grid-cols-4">
                {availableThemes.map(theme => (
                  <ThemeCard
                    key={theme.id}
                    theme={theme}
                    isSelected={formData.theme === theme.id}
                    onSelect={handleThemeSelect}
                    isLocked={!theme.available && theme.available !== undefined}
                  />
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="branding" className="space-y-6">
          <Card className="aurora-card">
            <CardHeader className="p-5 sm:p-6">
              <CardTitle className="font-heading text-base font-semibold">Brand Identity</CardTitle>
              <CardDescription>
                Set your app name, logo, and tagline
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 p-5 pt-0 sm:p-6 sm:pt-0">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="min-w-0 space-y-2">
                  <Label className={LABEL}>App Name</Label>
                  <Input
                    value={formData.app_name}
                    onChange={(e) => setFormData({ ...formData, app_name: e.target.value })}
                    placeholder="My Awesome Platform"
                    className={INPUT}
                  />
                </div>
                <div className="min-w-0 space-y-2">
                  <Label className={LABEL}>Short Name</Label>
                  <Input
                    value={formData.app_short_name}
                    onChange={(e) => setFormData({ ...formData, app_short_name: e.target.value })}
                    placeholder="MAP"
                    className={INPUT}
                  />
                  <p className="text-xs text-muted-foreground">Used on mobile devices</p>
                </div>
              </div>
              <div className="min-w-0 space-y-2">
                <Label className={LABEL}>Tagline</Label>
                <Input
                  value={formData.tagline}
                  onChange={(e) => setFormData({ ...formData, tagline: e.target.value })}
                  placeholder="Your social media automation partner"
                  className={INPUT}
                />
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="min-w-0 space-y-2">
                  <Label className={LABEL}>Logo URL</Label>
                  <Input
                    value={formData.logo_url}
                    onChange={(e) => setFormData({ ...formData, logo_url: e.target.value })}
                    placeholder="https://example.com/logo.png"
                    className={INPUT}
                  />
                  {formData.logo_url && (
                    <div className="flex items-center justify-center rounded-xl border border-border bg-secondary/60 p-4">
                      <img src={formData.logo_url} alt="Logo preview" className="h-12 max-w-full object-contain" />
                    </div>
                  )}
                </div>
                <div className="min-w-0 space-y-2">
                  <Label className={LABEL}>Favicon URL</Label>
                  <Input
                    value={formData.favicon_url}
                    onChange={(e) => setFormData({ ...formData, favicon_url: e.target.value })}
                    placeholder="https://example.com/favicon.ico"
                    className={INPUT}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="colors" className="space-y-6">
          {!planFeatures.color_picker ? (
            <Card className="aurora-card aurora-card-dashed">
              <CardContent className="p-8 text-center sm:p-10">
                <span className="mx-auto inline-flex h-14 w-14 items-center justify-center rounded-full bg-muted/40 text-muted-foreground/60 ring-1 ring-border">
                  <Lock className="h-6 w-6" />
                </span>
                <p className="mt-4 text-sm text-muted-foreground">Color customization requires Professional plan</p>
                <Button variant="outline" className="tap mt-5 rounded-full">Upgrade Plan</Button>
              </CardContent>
            </Card>
          ) : (
            <Card className="aurora-card">
              <CardHeader className="p-5 sm:p-6">
                <CardTitle className="font-heading text-base font-semibold">Custom Colors</CardTitle>
                <CardDescription>
                  Override theme colors with your brand colors
                </CardDescription>
              </CardHeader>
              <CardContent className="p-5 pt-0 sm:p-6 sm:pt-0">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6 lg:grid-cols-3">
                  <ColorPicker
                    label="Primary Color"
                    value={formData.colors.primary}
                    onChange={(v) => setFormData({ ...formData, colors: { ...formData.colors, primary: v } })}
                  />
                  <ColorPicker
                    label="Secondary Color"
                    value={formData.colors.secondary}
                    onChange={(v) => setFormData({ ...formData, colors: { ...formData.colors, secondary: v } })}
                  />
                  <ColorPicker
                    label="Accent Color"
                    value={formData.colors.accent}
                    onChange={(v) => setFormData({ ...formData, colors: { ...formData.colors, accent: v } })}
                  />
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="advanced" className="space-y-6">
          {!planFeatures.custom_css ? (
            <Card className="aurora-card aurora-card-dashed">
              <CardContent className="p-8 text-center sm:p-10">
                <span className="mx-auto inline-flex h-14 w-14 items-center justify-center rounded-full bg-muted/40 text-muted-foreground/60 ring-1 ring-border">
                  <Lock className="h-6 w-6" />
                </span>
                <p className="mt-4 text-sm text-muted-foreground">Custom CSS requires Enterprise plan</p>
                <Button variant="outline" className="tap mt-5 rounded-full">Upgrade Plan</Button>
              </CardContent>
            </Card>
          ) : (
            <Card className="aurora-card">
              <CardHeader className="p-5 sm:p-6">
                <CardTitle className="font-heading text-base font-semibold">Custom CSS</CardTitle>
                <CardDescription>
                  Add custom CSS to further customize your platform's appearance
                </CardDescription>
              </CardHeader>
              <CardContent className="p-5 pt-0 sm:p-6 sm:pt-0">
                <div className="space-y-2">
                  <Label className={LABEL}>CSS Code</Label>
                  <textarea
                    value={formData.custom_css}
                    onChange={(e) => setFormData({ ...formData, custom_css: e.target.value })}
                    placeholder={`/* Custom CSS */\n.my-class {\n  color: #fff;\n}`}
                    className="h-64 w-full resize-none rounded-xl border border-border bg-secondary/60 p-4 font-mono text-sm text-foreground/90 placeholder:text-muted-foreground/70"
                  />
                </div>
                <div className="mt-4 rounded-xl border border-warning/20 bg-warning/10 p-4">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-warning" />
                    <div className="min-w-0">
                      <p className="font-medium text-warning">Use with caution</p>
                      <p className="text-sm text-warning/80">Custom CSS can break your UI if not written correctly. Test thoroughly.</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

/**
 * The backend for this page does not exist on the Spring server yet (it still calls V2's API).
 * The banner is rendered ABOVE the original page on every path - loading, error, empty - so the
 * state is never mistaken for "no data". Remove the wrapper when the /uiapi endpoints land.
 */
export default function BrandingSettingsPage() {
  return (
    <>
      <NotWiredBanner what="Reseller branding" legacyUrl={null} legacyLabel={null} />
      <BrandingSettingsPageInner />
    </>
  );
}
